import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react'
import { isLocale, LOCALE_COOKIE, pickLocale } from '@shared/locale'
import { readPref, writePref } from '@/lib/storage'
import { appTitle, type Locale, type MessageKey, messages } from './messages'

/** The person's choice, mirrored where the Worker can read it (shared/locale.ts). */
function writeCookie(l: Locale) {
  const secure = location.protocol === 'https:' ? '; secure' : ''
  document.cookie = `${LOCALE_COOKIE}=${l}; path=/; max-age=31536000; samesite=lax${secure}`
}

function detect(): Locale {
  const saved = readPref('locale')
  if (isLocale(saved)) {
    writeCookie(saved)
    return saved
  }
  return pickLocale(navigator.languages.length ? navigator.languages : [navigator.language])
}

/** Set on <html> (so text is shaped for the language) and in the title. */
function applyLocale(l: Locale) {
  document.documentElement.lang = l
  if (document.title.startsWith('moot — ')) document.title = appTitle(l)
}

/** Decided once, before the first render. */
const initialLocale = detect()
applyLocale(initialLocale)

type I18n = {
  locale: Locale
  setLocale: (l: Locale) => void
  t: (key: MessageKey, vars?: Record<string, string | number>) => string
  relativeTime: (ms: number) => string
}

const Ctx = createContext<I18n | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)

  const setLocale = useCallback((l: Locale) => {
    writePref('locale', l)
    writeCookie(l)
    applyLocale(l)
    setLocaleState(l)
  }, [])

  const value = useMemo<I18n>(() => {
    const dict = messages[locale]
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    return {
      locale,
      setLocale,
      t: (key, vars) =>
        dict[key].replace(/\{(\w+)\}/g, (_, name: string) => String(vars?.[name] ?? '')),
      relativeTime: (ms) => {
        const diff = (ms - Date.now()) / 1000
        const units: [Intl.RelativeTimeFormatUnit, number][] = [
          ['year', 31536000],
          ['month', 2592000],
          ['week', 604800],
          ['day', 86400],
          ['hour', 3600],
          ['minute', 60],
        ]
        for (const [unit, secs] of units) {
          if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit)
        }
        return rtf.format(0, 'second')
      },
    }
  }, [locale, setLocale])

  return <Ctx value={value}>{children}</Ctx>
}

export function useI18n(): I18n {
  const v = use(Ctx)
  if (!v) throw new Error('useI18n outside I18nProvider')
  return v
}
